import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {BookOpen, Briefcase, MessageCircleDashed as MessageCircle, Users, Video, Globe, Award, Smartphone} from 'lucide-react'

const Benefits = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const benefits = [
    {
      icon: BookOpen,
      title: 'Cursos completos',
      description: 'Do básico ao avançado',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Briefcase,
      title: 'Cursos específicos',
      description: 'Viagem, carreira, áreas profissionais',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: MessageCircle,
      title: 'Lucy IA no WhatsApp',
      description: 'Tire dúvidas 24h por dia',
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: Users,
      title: 'Professores reais',
      description: 'Agendamento de dúvidas',
      color: 'from-orange-500 to-red-500',
    },
    {
      icon: Video,
      title: 'Aulões ao vivo',
      description: 'Todas as semanas',
      color: 'from-pink-500 to-rose-500',
    },
    {
      icon: Globe,
      title: 'Comunidade ativa',
      description: 'Troca de experiências',
      color: 'from-teal-500 to-cyan-500',
    },
    {
      icon: Award,
      title: 'Parceria TOEFL',
      description: 'Certificação internacional',
      color: 'from-yellow-500 to-orange-500',
    },
    {
      icon: Smartphone,
      title: 'Multi-plataforma',
      description: 'PC, Android ou iOS',
      color: 'from-indigo-500 to-purple-500',
    },
  ]

  return (
    <section ref={ref} className="py-20 md:py-32 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-tight">
              Tudo o que você precisa para{' '}
              <span className="bg-gradient-to-r from-[#0D7FA3] to-[#C64DAD] bg-clip-text text-transparent">
                finalmente sair do lugar
              </span>
            </h2>
          </motion.div>

          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12 mx-auto max-w-5xl">
            {benefits.map((benefit, index) => {
              const isLarge = index === 0 || index === 2
              const Icon = benefit.icon
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className={`
                    group relative bg-white rounded-3xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300
                    border-2 border-transparent hover:border-[#0D7FA3]/20
                    ${isLarge ? 'md:col-span-2 md:row-span-1' : 'md:col-span-2'}
                  `}
                >
                  <div className={`inline-flex p-3 rounded-2xl bg-gradient-to-br ${benefit.color} mb-4`}>
                    <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                  </div>
                  
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 group-hover:text-[#0D7FA3] transition-colors">
                    {benefit.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>

                  {/* Decorative gradient on hover */}
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#0D7FA3]/5 to-[#C64DAD]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                </motion.div>
              )
            })}
          </div>

          {/* Bottom statement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-8 md:p-12 text-center border-l-4 border-[#C64DAD]"
          >
            <p className="text-2xl md:text-3xl text-white font-bold mb-4 leading-relaxed">
              Nada de comprar módulos separados.
            </p>
            <p className="text-xl md:text-2xl text-yellow-300 font-bold">
              Tudo já está incluso na sua assinatura.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default Benefits
